import { Message, MessageBox } from "Components/MessageBox";
import React, { useState } from "react";
import { SearchButtonProps, SearchField } from "./SearchField";

import { Props as InputProps } from "@storeblocks/input";
import { isDefined } from "Utils/functions";
import { ResultSelect } from "./ResultSelect";
import { SearchError } from "./SearchError";
import { SelectedResult } from "./SelectedResult";
import { Result } from "./interfaces";
import { AutocompleteContainer } from "./styled";

interface SearchProps
  extends Omit<InputProps, "error" | "warning" | "success"> {
  button?: SearchButtonProps;
  error?: string;
  warning?: string;
  success?: string;
}

interface ResultProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  id: string;
  value: string;
  options: Result[];
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

export interface Props {
  className?: string;
  searchProps: SearchProps;
  resultProps?: ResultProps;
  isLoading?: boolean;
  onReset?: () => void;
  resetButtonContent?: React.ReactNode;
  autoSelectResults?: boolean;
  noResultsMessage?: Message;
  info?: string;
  success?: string;
  warning?: string;
  error?: string;
}

export const ObjectFinder = ({
  className,
  searchProps,
  resultProps,
  noResultsMessage,
  info,
  success,
  warning,
  error,
  isLoading,
  onReset,
  resetButtonContent,
  autoSelectResults = true,
  children,
}: React.PropsWithChildren<Props>): React.ReactElement => {
  const resultId = resultProps?.id ? `${resultProps.id}-` : "";
  const labelId = `${searchProps.id}-${resultId}label`;
  const hintId = `${searchProps.id}-${resultId}hint`;
  const listboxId = `${resultId}listbox`;

  const [shouldFocusSearchField, toggleShouldFocusSearchField] =
    useState(false);

  return (
    <AutocompleteContainer className={className}>
      {resultProps?.value ? (
        <SelectedResult
          {...resultProps}
          isLoading={isLoading}
          onReset={onReset}
          resetButtonContent={resetButtonContent}
          toggleShouldFocusSearchField={toggleShouldFocusSearchField}
        />
      ) : (
        <>
          <SearchField
            {...searchProps}
            labelId={labelId}
            hintId={hintId}
            listboxId={listboxId}
            isLoading={isLoading}
            searchResults={resultProps?.options ?? []}
            shouldFocusSearchField={shouldFocusSearchField}
            toggleShouldFocusSearchField={toggleShouldFocusSearchField}
          />
          {isDefined(resultProps) && (
            <ResultSelect
              {...resultProps}
              autoSelectResults={autoSelectResults}
              listboxId={listboxId}
              inputId={searchProps.id}
              labelledBy={labelId}
            />
          )}
          {isDefined(noResultsMessage) && <MessageBox {...noResultsMessage} />}
          <SearchError
            inputId={searchProps.id}
            info={info}
            success={success}
            warning={warning}
            error={error}
          />
        </>
      )}
      {children}
    </AutocompleteContainer>
  );
};
